/**
 * Error tracking (Sentry) and privacy-friendly analytics (Plausible). Both are OFF unless their build-time env var is set:
 *
 *   REACT_APP_SENTRY_DSN          Sentry DSN of the frontend project. Optional: REACT_APP_SENTRY_ENVIRONMENT,
 *                                 REACT_APP_SENTRY_RELEASE. No PII: no IPs / cookies / user, query strings stripped from
 *                                 every URL (tokens can travel there), console breadcrumbs dropped, secret-looking keys
 *                                 removed. The Sentry SDK is loaded lazily, only when the DSN is set.
 *   REACT_APP_ANALYTICS           the site's domain as registered with Plausible (e.g. "sonikly.ai"). Adds Plausible's
 *                                 script tag (cookieless, no personal data, so no cookie banner is needed); page views of
 *                                 the single-page app are tracked by the script itself. Optional:
 *                                 REACT_APP_ANALYTICS_SRC (a self-hosted / proxied script URL).
 */

const SECRET_KEY = /authorization|token|password|passwd|secret|license|cookie|session|api[-_]?key|email/i;

export function stripQuery(url) {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  return cut >= 0 ? url.slice(0, cut) : url;
}

function scrubObject(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 6) return obj;
  Object.keys(obj).forEach((k) => {
    if (SECRET_KEY.test(k)) delete obj[k]; // eslint-disable-line no-param-reassign
    else if (obj[k] && typeof obj[k] === 'object') scrubObject(obj[k], depth + 1);
    else if (typeof obj[k] === 'string' && /^https?:\/\//.test(obj[k])) obj[k] = stripQuery(obj[k]); // eslint-disable-line no-param-reassign
  });
  return obj;
}

export function scrubBreadcrumb(crumb) {
  if (!crumb) return crumb;
  if (crumb.category === 'console') return null; // console lines can hold anything
  if (crumb.data) {
    ['url', 'from', 'to'].forEach((k) => { if (crumb.data[k]) crumb.data[k] = stripQuery(crumb.data[k]); }); // eslint-disable-line no-param-reassign
    scrubObject(crumb.data);
  }
  return crumb;
}

export function scrubEvent(event) {
  if (!event) return event;
  if (event.request) {
    const req = event.request;
    if (req.url) req.url = stripQuery(req.url);
    delete req.cookies;
    delete req.query_string;
    delete req.data;
    if (req.headers) {
      const keep = {};
      Object.keys(req.headers).forEach((h) => {
        if (/^(user-agent|referer|accept-language)$/i.test(h)) keep[h] = /^referer$/i.test(h) ? stripQuery(req.headers[h]) : req.headers[h];
      });
      req.headers = keep;
    }
  }
  delete event.user; // eslint-disable-line no-param-reassign
  if (event.extra) scrubObject(event.extra);
  if (event.contexts) scrubObject(event.contexts);
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean); // eslint-disable-line no-param-reassign
  return event;
}

let sentryStarted = false;

/** Start Sentry when REACT_APP_SENTRY_DSN is set (lazy import). Resolves true when it is on. Never throws. */
export async function initSentry(env = process.env) {
  const dsn = (env.REACT_APP_SENTRY_DSN || '').trim();
  if (!dsn || sentryStarted) return sentryStarted;
  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn,
      environment: env.REACT_APP_SENTRY_ENVIRONMENT || env.NODE_ENV,
      release: env.REACT_APP_SENTRY_RELEASE || undefined,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
    });
    sentryStarted = true;
  } catch (e) {
    sentryStarted = false;
  }
  return sentryStarted;
}

/** Add Plausible's script when REACT_APP_ANALYTICS is set; returns true when analytics is on. */
export function initAnalytics(env = process.env, doc = typeof document !== 'undefined' ? document : null) {
  const domain = (env.REACT_APP_ANALYTICS || '').trim();
  if (!domain || !doc || (typeof window !== 'undefined' && window.location.pathname === '/reset-password')) return false;
  if (!doc.querySelector('script[data-sonikly-analytics]')) {
    const s = doc.createElement('script');
    s.defer = true;
    s.setAttribute('data-domain', domain);
    s.setAttribute('data-sonikly-analytics', '1');
    s.src = (env.REACT_APP_ANALYTICS_SRC || '').trim() || 'https://plausible.io/js/script.js';
    doc.head.appendChild(s);
  }
  // queue events fired before the script has loaded (Plausible's documented stub)
  window.plausible = window.plausible || function plausibleQueue(...args) { (window.plausible.q = window.plausible.q || []).push(args); };
  return true;
}

const SAFE_PROPS = ['method', 'form_type', 'file_type', 'audio_type', 'currency', 'value', 'synth', 'plan'];

/** A custom event (name + a few non-personal props) to Plausible, when analytics is on. */
export function trackAnalyticsEvent(name, props = {}, env = process.env) {
  if (!(env.REACT_APP_ANALYTICS || '').trim() || typeof window === 'undefined' || typeof window.plausible !== 'function') return false;
  const safe = {};
  SAFE_PROPS.forEach((k) => { if (props && ['string', 'number', 'boolean'].includes(typeof props[k])) safe[k] = props[k]; });
  window.plausible(name, Object.keys(safe).length ? { props: safe } : undefined);
  return true;
}
